export const get_api_error_message = (error, fallback_message) => {
  const response_data = error?.responseJSON || error;
  const message = response_data?.message;

  if (!message) {
    return fallback_message;
  }

  if (typeof message === "string") {
    return message;
  }

  return Object.values(message).flat().join(" ");
};

function get_error_value(error_message, key) {
  const field_map = {
    name: "name",
    category_id: "category_id",
    description: "description",
    goal_amount: "goal_amount",
    start_date: "start_date",
    end_date: "end_date",
  };
  const field_name = Reflect.get(field_map, key) || "";
  return get_error_message_value(error_message, field_name);
}

function get_error_message_value(error_message, field_name) {
  return Reflect.get(Reflect.get(error_message || {}, field_name) || {}, 0) ?? "";
}

export const map_campaign_form_errors = (error_message) => ({
  title: get_error_value(error_message, "name"),
  category_id: get_error_value(error_message, "category_id"),
  description: get_error_value(error_message, "description"),
  goal_amount: get_error_value(error_message, "goal_amount"),
  start_date: get_error_value(error_message, "start_date"),
  end_date: get_error_value(error_message, "end_date"),
});

function get_normalized_date(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

function is_valid_title(title) {
  return Boolean(String(title || "").trim());
}

function is_start_after_end(start_date, end_date) {
  return Boolean(start_date && end_date && start_date > end_date);
}

function is_end_in_past(end_date, today) {
  return Boolean(end_date && end_date < today);
}

function apply_campaign_title_validation(errors, campaign_form) {
  if (!is_valid_title(campaign_form?.title)) {
    errors.title = "Campaign title is required.";
  }
}

function apply_campaign_date_validation(errors, start_date, end_date, today) {
  if (is_start_after_end(start_date, end_date)) {
    errors.start_date = "Start date cannot be after the selected end date.";
    errors.end_date = "End date cannot be before start date.";
  }

  if (is_end_in_past(end_date, today)) {
    errors.end_date = "End date cannot be in the past.";
  }
}

export const validate_campaign_form = (campaign_form) => {
  const errors = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start_date = get_normalized_date(campaign_form?.start_date);
  const end_date = get_normalized_date(campaign_form?.end_date);

  apply_campaign_title_validation(errors, campaign_form);
  apply_campaign_date_validation(errors, start_date, end_date, today);

  return ({
    is_valid: !Object.keys(errors).length,
    errors,
  });
};

export const get_category_name = (categories, category_id, default_name = "") => {
  const category = categories.find((item) => String(item.id) === String(category_id));
  return category?.name || default_name;
};

export const get_empty_campaign_form = () => ({
  title: "",
  description: "",
  category_id: "",
  goal_amount: "",
  start_date: "",
  end_date: "",
});
function get_campaign_category_id(campaign) {
  const categoryId = campaign?.category_id;
  if (categoryId) {
    return categoryId;
  }

  return campaign?.category?.id ?? "";
}
function get_default_value(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return value;
}

export const get_campaign_form_values = (campaign = {}) => ({
  title: get_default_value(campaign.name),
  description: get_default_value(campaign.description),
  category_id: get_campaign_category_id(campaign),
  goal_amount: get_default_value(campaign.goal_amount),
  start_date: get_default_value(campaign.start_date),
  end_date: get_default_value(campaign.end_date),
});

export const matches_campaign_query = (campaign, query) => {
  if (!query) {
    return true;
  }

  const searchable_values = [
    campaign?.name,
    campaign?.category?.name,
    campaign?.description,
  ];

  return searchable_values.some((value) => String(value || "").toLowerCase().includes(query));
};

export const matches_campaign_filter = (campaign, filter_value) => filter_value === "all" || campaign?.status === filter_value;

function append_form_value(form_data, key, value) {
  if (value === "" || value === null || value === undefined) {
    return;
  }

  form_data.append(key, value);
}

function append_media_files(form_data, files = []) {
  files.forEach((file) => {
    if (file) {
      form_data.append("media_files", file);
    }
  });
}

function append_campaign_form_fields(form_data, campaign_form) {
  append_form_value(form_data, "category_id", campaign_form?.category_id);
  append_form_value(form_data, "goal_amount", campaign_form?.goal_amount);
  append_form_value(form_data, "start_date", campaign_form?.start_date);
  append_form_value(form_data, "end_date", campaign_form?.end_date);
}

function append_campaign_cover_image(form_data, campaign_form) {
  if (typeof File !== "undefined" && campaign_form?.cover_image instanceof File) {
    append_media_files(form_data, [campaign_form.cover_image]);
  }
}

export function build_campaign_form_data(campaign_form, supporting_files = []) {
  const form_data = new FormData();

  form_data.append("name", String(campaign_form?.title || "").trim());
  form_data.append("description", String(campaign_form?.description || "").trim());

  append_campaign_form_fields(form_data, campaign_form);
  append_campaign_cover_image(form_data, campaign_form);
  append_media_files(form_data, supporting_files);

  return form_data;
}

export function update_campaign_in_lists({
  updated_campaign,
  my_campaigns_data = [],
  public_campaigns = [],
}) {
  const next_my_campaigns = upsert_campaign_in_list(my_campaigns_data, updated_campaign);
  const next_public_campaigns = sync_public_campaigns(public_campaigns, updated_campaign);

  return {
    my_campaigns_data: next_my_campaigns,
    public_campaigns: next_public_campaigns,
    selected_campaign_id: updated_campaign.id,
  };
}

function upsert_campaign_in_list(campaigns, updated_campaign) {
  const next_campaigns = [...campaigns];
  const campaign_index = next_campaigns.findIndex(
    (campaign) => Number(campaign.id) === Number(updated_campaign.id)
  );

  if (campaign_index > -1) {
    next_campaigns.splice(campaign_index, 1, updated_campaign);
    return next_campaigns;
  }

  next_campaigns.unshift(updated_campaign);
  return next_campaigns;
}

function sync_public_campaigns(public_campaigns, updated_campaign) {
  const next_public_campaigns = [...public_campaigns];
  const public_campaign_index = next_public_campaigns.findIndex(
    (campaign) => Number(campaign.id) === Number(updated_campaign.id)
  );

  if (updated_campaign.status === "draft" && public_campaign_index > -1) {
    next_public_campaigns.splice(public_campaign_index, 1);
    return next_public_campaigns;
  }

  if (public_campaign_index > -1) {
    next_public_campaigns.splice(public_campaign_index, 1, updated_campaign);
    return next_public_campaigns;
  }

  next_public_campaigns.unshift(updated_campaign);
  return next_public_campaigns;
}
